import {useEffect, useState} from "react";
import {Helmet} from "react-helmet-async";
import {Navigate, useSearchParams} from "react-router";

import {useDateRange} from "@/components/datePicker/DatePickerURLRangeParamsContext.ts";
import {Last30Days} from "@/components/datePicker/subComponents/DatePickerShortcutList/DatePickerShortcutList.utils.ts";
import {GlobalPingBanner} from "@/components/globalPingBanner/GlobalPingBanner.tsx";
import ContentContainer from "@/components/layout/ContentContainer.tsx";
import {ErrorFallback} from "@/lib/handlers/ErrorFallback.tsx";
import {UpErrorBoundary} from "@/lib/handlers/UpErrorBoundary.tsx";
import {DEFAULT_OFFSET_KEY} from "@/lib/hooks/usePagination.ts";
import {getListTitle} from "@/lib/hooks/useTable.utils.ts";
import {CheckTypeDisplay} from "@/lib/types/checks.ts";
import {normalizeAlertsDeepLinkParams} from "@/pages/alerting/AlertsPage.utils.ts";
import {AlertActionBar} from "@/pages/alerting/components/list/AlertActionBar.tsx";
import {AlertList} from "@/pages/alerting/components/list/AlertsList.tsx";

export const ALERTS_RANGE_KEY_FROM = "range_from";
export const ALERTS_RANGE_KEY_TO = "range_to";
export const ALERTS_FIXED_RANGE_KEY = "range";

export const AlertsPage = () => {
  const [searchParams] = useSearchParams();
  const [isDeepLinkApplied, setIsDeepLinkApplied] = useState(
    () => normalizeAlertsDeepLinkParams(searchParams) === undefined
  );
  const {dateRange, fixedRangeOption, handleRangeChange} = useDateRange({
    fromKey: ALERTS_RANGE_KEY_FROM,
    toKey: ALERTS_RANGE_KEY_TO,
    fixedRangeKey: ALERTS_FIXED_RANGE_KEY,
    withDefault: Last30Days,
    clearKeysOnRangeChange: [DEFAULT_OFFSET_KEY],
  });

  // A deep link is rewritten once, before AlertList mounts: `pop` stays in the URL for the details modal, so
  // re-checking it later would re-apply the link over the user's own search and filters. A visit without one never
  // checks.
  const deepLinkSearch = isDeepLinkApplied ? undefined : normalizeAlertsDeepLinkParams(searchParams)?.toString();
  useEffect(() => {
    if (deepLinkSearch === undefined) {
      setIsDeepLinkApplied(true);
    }
  }, [deepLinkSearch]);
  const alertsTitle = getListTitle(searchParams, "Alerts", {key: "service_monitoring_type", display: CheckTypeDisplay});

  return (
    <UpErrorBoundary
      fallback={
        <ContentContainer title="Alerts">
          <ErrorFallback />
        </ContentContainer>
      }
    >
      <ContentContainer
        title={alertsTitle}
        banner={<GlobalPingBanner />}
        actionsBar={
          <AlertActionBar
            visibleRange={dateRange}
            onVisibleRangeChange={handleRangeChange}
            fixedRange={fixedRangeOption?.value}
          />
        }
      >
        <Helmet>
          <title>Alerts</title>
        </Helmet>
        {deepLinkSearch === undefined ? (
          <AlertList visibleRange={dateRange} />
        ) : (
          <Navigate replace to={{search: deepLinkSearch}} />
        )}
      </ContentContainer>
    </UpErrorBoundary>
  );
};
